import Grid from '@mui/material/Grid'
import capitalize from 'lodash/capitalize'
import React, { useCallback, useMemo } from 'react'

import { useGetBusinessUnitsQuery } from 'api/business-units/businessUnits'
import HHTextField from 'components/form-fields/v5/HHTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import T from 'T'

import type { SxProps, Theme } from '@mui/material/styles'

type BusinessUnitDropdownProps = {
  value?: string
  onChange: (businessUnitId: string) => void
  disabled?: boolean
  required?: boolean
  // Only where an empty value means "no unit": an account update treats a blank as "leave it", so None there does nothing.
  allowNone?: boolean
  errorText?: string
  menuSx?: SxProps<Theme>
}

// A load failure shows the field in an error state, never hidden, so it is not mistaken for "no units".
const BusinessUnitDropdown = ({
  value = '',
  onChange,
  disabled = false,
  required = false,
  allowNone = false,
  errorText = '',
  menuSx = undefined,
}: BusinessUnitDropdownProps) => {
  const { data: unsortedUnits = [], isLoading, isError } = useGetBusinessUnitsQuery()
  const businessUnits = useMemo(() => [...unsortedUnits].sort((a, b) => a.name.localeCompare(b.name, 'en-US')), [unsortedUnits])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value), [onChange])

  if (isLoading) {
    return null
  }

  if (isError) {
    return (
      <Grid size={12}>
        <HHTextField
          disabled
          fullWidth
          deprecatedLabel={false}
          error={T.BUSINESS_UNITS_LOAD_ERROR}
          helperText={T.BUSINESS_UNITS_LOAD_ERROR}
          label={capitalize(T.BUSINESS_UNIT)}
          value=""
        />
      </Grid>
    )
  }

  if (businessUnits.length === 0) {
    return null
  }

  return (
    <Grid size={12}>
      <HHTextField
        fullWidth
        select
        deprecatedLabel={false}
        disabled={disabled}
        error={errorText}
        helperText={errorText}
        label={required ? `${capitalize(T.BUSINESS_UNIT)}*` : capitalize(T.BUSINESS_UNIT)}
        required={required}
        SelectProps={{ MenuProps: { sx: menuSx } }}
        value={value}
        onChange={handleChange}
      >
        {allowNone && !required && (
          <SelectItem key="no-business-unit" value="">
            {T.NONE}
          </SelectItem>
        )}
        {businessUnits.map(unit => (
          <SelectItem key={unit.id} value={unit.id}>
            {unit.name}
          </SelectItem>
        ))}
      </HHTextField>
    </Grid>
  )
}

export default BusinessUnitDropdown
